// frontend/src/components/FinanceReport/Accounting/allocations/AllocationRow.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { money } from "./helpers";
import OrderCodeInput from "./OrderCodeInput";

export default function AllocationRow({
  breakdown,
  currenciesById,
  paymentCurrency,
  paymentId,
  isEditing,
  editDraft,
  setEditDraft,
  editError,
  editSaving,
  clientOrders,
  currencies,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onDelete,
  onOpenDocs,
}) {
  const navigate = useNavigate();
  const b = breakdown;
  const a = b.raw;
  const currency = currenciesById[a.currency] || paymentCurrency || null;
  const orderId = a.order && typeof a.order === "object" ? a.order.id : a.order;

  const segs = [];
  (a.our_allocations || []).forEach((oa) => {
    segs.push(
      `Наш заказ ${oa.order_code || `#${oa.order}`}: ${money(oa.amount, currency)}`
    );
  });
  if (b.profit > 0) segs.push(`Прибыль: ${money(b.profit, currency)}`);
  if (b.supplier > 0) segs.push(`Поставщику: ${money(b.supplier, currency)}`);
  const distText = segs.length ? segs.join(" · ") : "—";

  const handleRowClick = (e) => {
    if (isEditing) return;
    if (
      e.target.closest("button") ||
      e.target.closest("a") ||
      e.target.tagName === "INPUT" ||
      e.target.tagName === "SELECT"
    ) {
      return;
    }
    navigate(`/accounting/${paymentId}/allocations/${a.id}`);
  };

  const rowClass = isEditing
    ? "border-t bg-yellow-50"
    : b.complete
    ? "border-t bg-emerald-50 hover:bg-emerald-100 cursor-pointer"
    : "border-t bg-red-50 hover:bg-red-100 cursor-pointer";

  return (
    <tr onClick={handleRowClick} className={`transition-colors ${rowClass}`}>
      {/* Дата */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            type="date"
            className="w-full rounded border px-1 py-0.5 text-xs"
            value={editDraft.date}
            onChange={(e) =>
              setEditDraft((c) => ({ ...c, date: e.target.value }))
            }
          />
        ) : a.date ? (
          new Date(`${a.date}T00:00:00`).toLocaleDateString("ru-RU")
        ) : (
          "—"
        )}
      </td>

      {/* Тип */}
      <td className="border p-2 align-top">
        <span
          className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
            b.complete
              ? "bg-emerald-100 text-emerald-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {b.complete ? "✓ Платёж фабрике" : "✕ Платёж фабрике"}
        </span>
      </td>

      {/* Заказ */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <OrderCodeInput
            id={`edit-alloc-${a.id}`}
            orders={clientOrders}
            value={editDraft.orderCode}
            onChange={(v) => setEditDraft((c) => ({ ...c, orderCode: v }))}
          />
        ) : orderId ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/orders/${orderId}`);
            }}
            className="text-blue-700 hover:underline"
          >
            {a.order_code || `#${a.order}`}
          </button>
        ) : (
          a.order_code || "—"
        )}
      </td>

      {/* Счёт заказа */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <span className="text-xs text-slate-500">—</span>
        ) : orderId ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              navigate(`/orders/${orderId}/finances`);
            }}
            className="text-left text-xs text-blue-700 hover:underline"
          >
            Счёт заказа {a.order_code || `#${a.order}`}
          </button>
        ) : (
          "—"
        )}
      </td>

      {/* Сумма + валюта */}
      <td className="border p-2 align-top font-semibold text-slate-900">
        {isEditing ? (
          <div className="flex gap-1">
            <input
              type="number"
              onWheel={(e) => e.currentTarget.blur()}
              min="0.01"
              step="0.01"
              value={editDraft.amount}
              onChange={(e) =>
                setEditDraft((c) => ({ ...c, amount: e.target.value }))
              }
              className="w-full rounded border px-1 py-0.5 text-xs"
            />
            <select
              value={editDraft.currency}
              onChange={(e) =>
                setEditDraft((c) => ({ ...c, currency: e.target.value }))
              }
              className="w-20 rounded border px-1 py-0.5 text-xs"
            >
              <option value="">—</option>
              {currencies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.symbol} {c.code}
                </option>
              ))}
            </select>
          </div>
        ) : (
          money(b.amount, currency)
        )}
      </td>

      {/* Распределение */}
      <td className="border p-2 align-top">
        <div className="text-xs leading-5">
          <div className="text-slate-700">{distText}</div>
          <div
            className={`font-semibold ${
              b.complete ? "text-emerald-700" : "text-red-700"
            }`}
          >
            Остаток: {money(b.remaining, currency)}
          </div>
        </div>
      </td>

      {/* Статус */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <select
            value={editDraft.payment_status}
            onChange={(e) =>
              setEditDraft((c) => ({ ...c, payment_status: e.target.value }))
            }
            className="w-full rounded border px-1 py-0.5 text-xs"
          >
            <option value="PENDING">Ожидает</option>
            <option value="PAID">Оплачен</option>
          </select>
        ) : (
          <span
            className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${
              a.payment_status === "PAID"
                ? "bg-emerald-100 text-emerald-700"
                : "bg-amber-100 text-amber-700"
            }`}
          >
            {a.payment_status === "PAID" ? "Оплачен" : "Ожидает"}
          </span>
        )}
      </td>

      {/* Комментарий */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            value={editDraft.comment}
            onChange={(e) =>
              setEditDraft((c) => ({ ...c, comment: e.target.value }))
            }
            className="w-full rounded border px-1 py-0.5 text-xs"
            placeholder="Коммент."
          />
        ) : (
          <span
            className="block max-w-[16rem] truncate text-xs text-slate-600"
            title={a.comment || ""}
          >
            {a.comment || "—"}
          </span>
        )}
      </td>

      {/* Платёж через */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <input
            value={editDraft.payment_agent}
            onChange={(e) =>
              setEditDraft((c) => ({ ...c, payment_agent: e.target.value }))
            }
            className="w-full rounded border px-1 py-0.5 text-xs"
            placeholder="Через"
          />
        ) : (
          <span className="text-xs text-slate-600">
            {a.payment_agent || "—"}
          </span>
        )}
      </td>

      {/* Документы */}
      <td className="border p-2 align-top">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenDocs({ item: a, type: "allocation" });
          }}
          className="text-xs text-blue-700 underline hover:text-blue-900"
        >
          📎 ({a.documents?.length || 0})
        </button>
      </td>

      {/* Действия */}
      <td className="border p-2 align-top">
        {isEditing ? (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onSaveEdit();
              }}
              disabled={editSaving}
              className="rounded bg-blue-600 px-2 py-1 text-xs font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {editSaving ? "…" : "Сохранить"}
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onCancelEdit();
              }}
              disabled={editSaving}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              Отмена
            </button>
            {editError && (
              <div className="mt-1 w-full text-xs text-red-600">{editError}</div>
            )}
          </div>
        ) : (
          <div className="flex flex-wrap gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onStartEdit(a);
              }}
              className="rounded border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
              title="Редактировать"
            >
              ✏
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(a);
              }}
              className="rounded border border-red-200 bg-white px-2 py-1 text-xs text-red-600 hover:bg-red-50"
              title="Удалить"
            >
              🗑
            </button>
          </div>
        )}
      </td>
    </tr>
  );
}